import { PERSPECTIVE_TRANSFORM_LENGTH, perspectiveTransformProblem, } from './perspectiveTransform.validation';
export const EMPTY_ONBOARDING_VALIDATION_CONTEXT = {
    existingFacilityIds: [],
    existingFacilityNames: [],
    existingPitchIds: [],
    existingDeviceIds: [],
};
const TIME_PATTERN = /^\d{2}:\d{2}:\d{2}$/;
const HHMM_PATTERN = /^\d{4}$/;
const normalise = (value) => value.trim().replace(/\s+/g, ' ').toLowerCase();
const isPositiveInteger = (value) => value !== null && Number.isInteger(value) && value > 0;
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
class Issues {
    constructor() {
        this.errors = [];
        this.warnings = [];
    }
    error(path, message) {
        this.errors.push({ path, message, severity: 'error' });
    }
    conflict(path, message) {
        this.errors.push({ path, message, severity: 'error', code: 'conflict' });
    }
    warn(path, message) {
        this.warnings.push({ path, message, severity: 'warning' });
    }
}
const validateFacility = (draft, context, issues) => {
    const { facility } = draft;
    if (!facility.name.trim()) {
        issues.error('facility.name', 'Facility name is required.');
    }
    else {
        const wanted = normalise(facility.name);
        if (context.existingFacilityNames.some(name => normalise(name) === wanted)) {
            issues.conflict('facility.name', `A facility called "${facility.name.trim()}" already exists. ` +
                'Onboarding can only create new facilities — adding a pitch to an existing ' +
                'facility is not supported yet.');
        }
        else {
            const similar = context.existingFacilityNames.filter(name => {
                const other = normalise(name);
                return other.startsWith(wanted) || wanted.startsWith(other);
            });
            for (const name of similar) {
                issues.warn('facility.name', `An existing facility is named "${name}", which is very similar. ` +
                    'Check this is genuinely a different site.');
            }
        }
    }
    if (!isPositiveInteger(facility.id)) {
        issues.error('facility.id', 'Facility ID must be a positive whole number.');
    }
    else if (context.existingFacilityIds.includes(facility.id)) {
        issues.conflict('facility.id', `Facility ID ${facility.id} is already taken. Pick another.`);
    }
};
const validatePitchIdentity = (draft, context, issues) => {
    const seenPitchIds = new Map();
    const seenDeviceIds = new Map();
    draft.pitches.forEach((pitch, index) => {
        const at = `pitches.${index}`;
        if (!isPositiveInteger(pitch.id)) {
            issues.error(`${at}.id`, 'Pitch ID must be a positive whole number.');
        }
        else if (context.existingPitchIds.includes(pitch.id)) {
            issues.conflict(`${at}.id`, `Pitch ID ${pitch.id} is already taken.`);
        }
        else {
            const firstSeenAt = seenPitchIds.get(pitch.id);
            if (firstSeenAt !== undefined) {
                issues.error(`${at}.id`, `Pitch ID ${pitch.id} is used twice in this draft ` +
                    `(also on pitch ${firstSeenAt + 1}).`);
            }
            else {
                seenPitchIds.set(pitch.id, index);
            }
        }
        if (!pitch.name.trim()) {
            issues.error(`${at}.name`, 'Pitch name is required.');
        }
        if (pitch.sensors.length === 0) {
            issues.error(`${at}.sensors`, 'A pitch needs at least one sensor.');
        }
        pitch.sensors.forEach((sensor, sensorIndex) => {
            const sensorAt = `${at}.sensors.${sensorIndex}`;
            if (sensor.id === null || !isPositiveInteger(sensor.id)) {
                issues.error(`${sensorAt}.id`, 'Sensor ID is required.');
            }
            else if (context.existingDeviceIds.includes(sensor.id)) {
                issues.conflict(`${sensorAt}.id`, `Sensor ID ${sensor.id} already belongs to another pitch.`);
            }
            else {
                const firstSeenAt = seenDeviceIds.get(sensor.id);
                if (firstSeenAt !== undefined) {
                    issues.error(`${sensorAt}.id`, `Sensor ID ${sensor.id} is used twice in this draft (also on ${firstSeenAt}).`);
                }
                else {
                    seenDeviceIds.set(sensor.id, sensorAt);
                }
            }
            if (sensor.lensSpecMillimeters === null) {
                issues.error(`${sensorAt}.lensSpecMillimeters`, 'Lens spec is required — check the lens on the installed sensor.');
            }
            else if (!Number.isFinite(sensor.lensSpecMillimeters) ||
                sensor.lensSpecMillimeters <= 0) {
                issues.error(`${sensorAt}.lensSpecMillimeters`, 'Lens spec must be a positive number of millimetres.');
            }
            if (sensor.perspectiveTransform === null) {
                issues.error(`${sensorAt}.perspectiveTransform`, `Perspective transform is required — paste the ${PERSPECTIVE_TRANSFORM_LENGTH} values.`);
            }
            else {
                const problem = perspectiveTransformProblem(sensor.perspectiveTransform);
                if (problem) {
                    issues.error(`${sensorAt}.perspectiveTransform`, problem);
                }
            }
            if (!sensor.latLong) {
                issues.warn(`${sensorAt}.latLong`, 'No coordinates for this sensor.');
            }
        });
    });
    const numbers = draft.pitches.map(pitch => pitch.facilityPitchNumber);
    if (new Set(numbers).size !== numbers.length) {
        issues.error('pitches', 'Each pitch needs a distinct pitch number within the facility.');
    }
};
const validatePitchDetail = (draft, issues) => {
    draft.pitches.forEach((pitch, index) => {
        const at = `pitches.${index}`;
        if (!pitch.latLong ||
            pitch.latLong.length !== 2 ||
            !pitch.latLong.every(isFiniteNumber)) {
            issues.error(`${at}.latLong`, 'Pitch coordinates are required, as a latitude and a longitude.');
        }
        else {
            const [latitude, longitude] = pitch.latLong;
            if (latitude < -90 || latitude > 90) {
                issues.error(`${at}.latLong`, `Latitude ${latitude} is outside -90 to 90 — the two may be the wrong way round.`);
            }
            else if (longitude < -180 || longitude > 180) {
                issues.error(`${at}.latLong`, `Longitude ${longitude} is outside -180 to 180.`);
            }
        }
        if (!isPositiveInteger(pitch.facilityPitchNumber)) {
            issues.error(`${at}.facilityPitchNumber`, 'Pitch number within the facility must be a positive whole number.');
        }
        for (const axis of ['x', 'y']) {
            if (!isPositiveInteger(pitch.pitchSizePixels[axis])) {
                issues.error(`${at}.pitchSizePixels.${axis}`, `Image ${axis.toUpperCase()} size must be a positive whole number of pixels.`);
            }
        }
        if (!isPositiveInteger(pitch.benchmarkPlayerCount)) {
            issues.error(`${at}.benchmarkPlayerCount`, 'Benchmark number of players must be a positive whole number.');
        }
        if (!isPositiveInteger(pitch.tooManyPlayersAlert)) {
            issues.error(`${at}.tooManyPlayersAlert`, "The 'too many players' alert threshold must be a positive whole number.");
        }
        if (pitch.sports.length === 0) {
            issues.warn(`${at}.sports`, 'No sport selected — this pitch will have no sport recorded.');
        }
        for (const unknown of pitch.unrecognisedSports) {
            issues.warn(`${at}.sports`, `"${unknown}" is not a recognised sport and will not be recorded.`);
        }
        for (const axis of ['x', 'y']) {
            const value = pitch.pitchCenterPixels[axis];
            if (value === null || !isFiniteNumber(value)) {
                issues.error(`${at}.pitchCenterPixels.${axis}`, `Pitch centre ${axis.toUpperCase()} is required — measure it off the facility ` +
                    'overview image.');
            }
            else if (value <= 0) {
                issues.error(`${at}.pitchCenterPixels.${axis}`, `Pitch centre ${axis.toUpperCase()} cannot be ${value} — that is the corner ` +
                    'of the overview image, not the middle of a pitch.');
            }
            else if (isPositiveInteger(pitch.pitchSizePixels[axis]) &&
                value > pitch.pitchSizePixels[axis]) {
                issues.error(`${at}.pitchCenterPixels.${axis}`, `Pitch centre ${axis.toUpperCase()} of ${value} is outside the image size ` +
                    `of ${pitch.pitchSizePixels[axis]} px — the centre and size must be ` +
                    'measured off the same overview image.');
            }
        }
        for (const [axis, value] of Object.entries(pitch.dimensionsMeters)) {
            if (!isFiniteNumber(value) || value <= 0) {
                issues.error(`${at}.dimensionsMeters.${axis}`, 'Pitch dimensions must be positive numbers of metres.');
            }
        }
        for (const [day, window] of Object.entries(pitch.outOfHours)) {
            for (const [edge, value] of Object.entries(window)) {
                if (!HHMM_PATTERN.test(value)) {
                    issues.error(`${at}.outOfHours.${day}.${edge}`, `Operating window ${edge} for ${day} is not a valid time.`);
                }
            }
        }
        pitch.snapshotTimes.forEach((time, timeIndex) => {
            if (!TIME_PATTERN.test(time)) {
                issues.error(`${at}.snapshotTimes.${timeIndex}`, `"${time}" is not a valid snapshot time (expected HH:MM:SS).`);
            }
        });
        if (new Set(pitch.snapshotTimes).size !== pitch.snapshotTimes.length) {
            issues.error(`${at}.snapshotTimes`, 'Snapshot times must be distinct.');
        }
        pitch.maintenanceSchedules.forEach((schedule, scheduleIndex) => {
            const scheduleAt = `${at}.maintenanceSchedules.${scheduleIndex}`;
            if (!schedule.type.trim()) {
                issues.error(`${scheduleAt}.type`, 'Schedule type is required.');
            }
            if (!/^\d{4}-\d{2}-\d{2}$/.test(schedule.startDate)) {
                issues.error(`${scheduleAt}.startDate`, 'Schedule needs a valid start date.');
            }
            if (!isPositiveInteger(schedule.expectedCompletionTimeMins)) {
                issues.error(`${scheduleAt}.expectedCompletionTimeMins`, 'Expected completion time must be a positive number of minutes.');
            }
            if (schedule.noticePeriodDays === null ||
                !Number.isInteger(schedule.noticePeriodDays) ||
                schedule.noticePeriodDays < 0) {
                issues.error(`${scheduleAt}.noticePeriodDays`, 'Notice period must be a whole number of days.');
            }
            schedule.usageRanges.forEach((range, rangeIndex) => {
                const rangeAt = `${scheduleAt}.usageRanges.${rangeIndex}`;
                const [from, to] = range.hours;
                if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
                    issues.error(rangeAt, 'Usage range must run from a lower to a higher hour.');
                }
                if (from < 0 || to < 0) {
                    issues.error(rangeAt, 'Weekly usage hours cannot be negative.');
                }
                if (!Number.isFinite(range.frequencyPerWeek) ||
                    range.frequencyPerWeek < 0) {
                    issues.error(`${rangeAt}.frequencyPerWeek`, 'Frequency must not be negative.');
                }
                const overlapping = schedule.usageRanges.findIndex((other, otherIndex) => otherIndex < rangeIndex &&
                    Math.min(other.hours[1], to) > Math.max(other.hours[0], from));
                if (overlapping !== -1) {
                    issues.warn(rangeAt, `This usage range overlaps range ${overlapping + 1}, so it is ambiguous ` + 'which frequency applies.');
                }
            });
        });
    });
};
export const validateOnboardingDraft = (draft, context = EMPTY_ONBOARDING_VALIDATION_CONTEXT) => {
    const issues = new Issues();
    if (!draft?.facility || !Array.isArray(draft.pitches) || !draft.meta) {
        issues.error('', 'This is not an onboarding draft.');
        return { errors: issues.errors, warnings: issues.warnings };
    }
    if (draft.pitches.length === 0) {
        issues.error('pitches', 'A facility needs at least one pitch.');
    }
    validateFacility(draft, context, issues);
    validatePitchIdentity(draft, context, issues);
    validatePitchDetail(draft, issues);
    if (draft.meta.timesProvidedIn &&
        !/local/i.test(draft.meta.timesProvidedIn)) {
        issues.warn('meta.timesProvidedIn', `Times were submitted in "${draft.meta.timesProvidedIn}" — no conversion is ` +
            'applied, so adjust them by hand if needed.');
    }
    return { errors: issues.errors, warnings: issues.warnings };
};
//# sourceMappingURL=onboardingDraft.validation.js.map